/**
 * Trial deep links: /free-signup?number=%2B1…&country=US&numberType=local
 * (optionally &plan=lite|starter|growth&billing=monthly|annual).
 *
 * limephone.io's number picker lives on another origin, so its
 * sessionStorage.selectedPhoneNumber never reached the dashboard — the number
 * has to travel in the query string. Parsing and validation are the order
 * flow's own (orderDeepLink.parseStartParams: strict E.164, the country must
 * match the number's calling code and region), so a trial link and a /start
 * link accept and refuse exactly the same numbers.
 *
 * Persistence has to survive the two round trips between landing and the
 * number step:
 *   - email OTP: same page, same tab — sessionStorage is enough;
 *   - Google: /api/auth/google → accounts.google.com → /auth/register?googleGrant
 *     — same tab, so sessionStorage survives, but the callback DROPS the query
 *     string, so it must already be stored before the user leaves.
 * A copy also goes to localStorage with a 24h expiry, for the person who
 * finishes in a new tab (an emailed link, a reopened browser).
 *
 * Nothing here is trusted for money or ownership: the number step re-checks
 * availability (exact match) before offering it, and the server buys the
 * number only at trial_activation. The plan is carried for display only —
 * what the trial converts into is the application's own plan.
 */
import { parseStartParams, parsePlanRequest, type PlanRequest, type StartNumberType } from "./orderDeepLink";

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export const TRIAL_LINK_KEY = "lp_trial_link";
/** The legacy same-origin handoff the number step has always read. */
const LEGACY_NUMBER_KEY = "selectedPhoneNumber";
const LEGACY_COUNTRY_KEY = "selectedCountry";
export const TRIAL_LINK_TTL_MS = 24 * 60 * 60 * 1000;

/** Trials are on US/Canadian numbers (the number step searches only these). */
const TRIAL_COUNTRIES: ReadonlySet<string> = new Set(["US", "CA"]);

export interface TrialLink {
  number: string | null;
  country: string | null;
  numberType: StartNumberType;
  plan: PlanRequest | null;
  /** The link named a number we cannot use for a trial (malformed, or not US/CA). */
  numberRejected: boolean;
  savedAt: number;
}

/** The query string → a validated link, or null when it carries nothing for us. */
export function parseTrialLink(params: URLSearchParams, now: number = Date.now()): TrialLink | null {
  if (!params.get("number") && !params.get("plan")) return null;
  const p = parseStartParams(params);
  let number = p.number;
  let numberRejected = p.numberRejected;
  if (number && (!p.country || !TRIAL_COUNTRIES.has(p.country))) {
    number = null;
    numberRejected = true;
  }
  // Mobile numbers are not offered on a trial; local and toll-free are.
  const numberType: StartNumberType = p.numberType === "tollFree" ? "tollFree" : "local";
  if (!number && !p.plan && !numberRejected) return null;
  return { number, country: number ? p.country : null, numberType, plan: p.plan, numberRejected, savedAt: now };
}

function readFrom(store: Store | null | undefined, now: number, ttl: boolean): TrialLink | null {
  if (!store) return null;
  try {
    const raw = store.getItem(TRIAL_LINK_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v !== "object" || typeof v.savedAt !== "number") return null;
    if (ttl && now - v.savedAt > TRIAL_LINK_TTL_MS) return null;
    // Re-validate what we stored: storage is writable by any script on the
    // origin, so it goes through the same parser as the link did.
    const q = new URLSearchParams();
    if (typeof v.number === "string") q.set("number", v.number);
    if (typeof v.country === "string") q.set("country", v.country);
    if (typeof v.numberType === "string") q.set("numberType", v.numberType);
    const parsed = parseTrialLink(q, v.savedAt);
    const plan = v.plan && typeof v.plan === "object"
      ? (v.plan.kind === "tier" ? parsePlanRequest(v.plan.tier, v.plan.interval === "year" ? "annual" : "monthly")
        : v.plan.kind === "id" ? parsePlanRequest(v.plan.id, null) : null)
      : null;
    return {
      number: parsed?.number ?? null,
      country: parsed?.country ?? null,
      numberType: parsed?.numberType ?? "local",
      plan,
      numberRejected: !!v.numberRejected || !!parsed?.numberRejected,
      savedAt: v.savedAt,
    };
  } catch {
    return null;
  }
}

/**
 * Capture a link from the current URL into both stores. A link replaces any
 * older one. Returns what was stored (or null when the URL carried nothing).
 */
export function captureTrialLink(
  params: URLSearchParams,
  stores: { session?: Store | null; local?: Store | null },
  now: number = Date.now(),
): TrialLink | null {
  const link = parseTrialLink(params, now);
  if (!link) return null;
  const json = JSON.stringify(link);
  for (const s of [stores.session, stores.local]) {
    try { s?.setItem(TRIAL_LINK_KEY, json); } catch { /* storage blocked: the picker still works */ }
  }
  // Keep the legacy keys in step, so nothing that still reads them disagrees.
  try {
    if (link.number) {
      stores.session?.setItem(LEGACY_NUMBER_KEY, link.number);
      if (link.country) stores.session?.setItem(LEGACY_COUNTRY_KEY, link.country);
    }
  } catch { /* ignore */ }
  return link;
}

/** The stored link: this tab's first, else a recent one from localStorage. */
export function readTrialLink(
  stores: { session?: Store | null; local?: Store | null },
  now: number = Date.now(),
): TrialLink | null {
  return readFrom(stores.session, now, false) ?? readFrom(stores.local, now, true);
}

export function clearTrialLink(stores: { session?: Store | null; local?: Store | null }): void {
  for (const s of [stores.session, stores.local]) {
    try { s?.removeItem(TRIAL_LINK_KEY); } catch { /* ignore */ }
  }
  try {
    stores.session?.removeItem(LEGACY_NUMBER_KEY);
    stores.session?.removeItem(LEGACY_COUNTRY_KEY);
  } catch { /* ignore */ }
}

export interface CarriedNumber {
  phoneNumber: string;
  country: string;
  numberType: StartNumberType;
  areaCode: string;
  /** "(989) 502-8631" for NANP; the E.164 otherwise. */
  formatted: string;
  /** Came from a /free-signup link (true) or the legacy same-origin key. */
  fromLink: boolean;
}

function nanpFormat(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/**
 * The number the visitor brought with them, if any: a deep link first, then
 * the legacy key the same-origin number picker writes (validated the same way).
 */
export function carriedNumber(
  stores: { session?: Store | null; local?: Store | null },
  now: number = Date.now(),
): CarriedNumber | null {
  const link = readTrialLink(stores, now);
  let number = link?.number ?? null;
  let country = link?.country ?? null;
  let numberType: StartNumberType = link?.numberType ?? "local";
  let fromLink = !!number;
  if (!number) {
    try {
      const raw = stores.session?.getItem(LEGACY_NUMBER_KEY) || "";
      const digits = raw.replace(/\D/g, "");
      const e164 = raw.trim().startsWith("+") ? `+${digits}` : digits.length === 10 ? `+1${digits}` : `+${digits}`;
      const q = new URLSearchParams({ number: e164 });
      const c = stores.session?.getItem(LEGACY_COUNTRY_KEY);
      if (c) q.set("country", c);
      const parsed = parseTrialLink(q, now);
      if (parsed?.number) {
        number = parsed.number;
        country = parsed.country;
        numberType = parsed.numberType;
        fromLink = false;
      }
    } catch { /* storage blocked */ }
  }
  if (!number || !country) return null;
  return {
    phoneNumber: number,
    country,
    numberType,
    areaCode: number.startsWith("+1") ? number.slice(2, 5) : "",
    formatted: nanpFormat(number),
    fromLink,
  };
}

/** The browser's stores, or nulls where they are blocked. */
export function browserStores(): { session: Store | null; local: Store | null } {
  let session: Store | null = null;
  let local: Store | null = null;
  try { session = window.sessionStorage; } catch { /* blocked */ }
  try { local = window.localStorage; } catch { /* blocked */ }
  return { session, local };
}
